import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { HiEye, HiEyeSlash } from 'react-icons/hi2';
import Modal from './Modal';
import Button from './Button';
import DayTableSimple from '../DayTableSimple';
import WeekNavigator from '../WeekNavigator';
import PeriodManager from '../../services/PeriodManager';
import ScheduleEntry from '../../models/ScheduleEntry';
import { SharedScrollProvider } from '../../contexts/SharedScrollContext';
import { KEY_SEPARATOR } from '../../hooks/useScheduleDragAndDrop';
import { createEmptyDraggedEntry } from '../../types/dragAndDrop';
import { deriveScheduleTimelineContext } from '../../utils/scheduleContext';
import { formatWeekLabel, getWeekNumber, getWeekStartDate } from '../../utils/helpers';

const EMPTY_DRAG_INFO = createEmptyDraggedEntry();
const noop = () => { };

const isPlaceholderEntry = (entry) => {
    if (!entry) return false;
    const data = typeof entry.toObject === 'function' ? entry.toObject() : entry;
    const name = (data?.displayName || data?.course || data?.title || '').toString().trim();
    if (name === '__PLACEHOLDER__') return true;
    if (data?.type === 'placeholder') return true;
    if (data?.additionalProps?.isPlaceholder === true) return true;
    return false;
};

const mergeUniqueEntries = (groups) => {
    const merged = [];
    const seenIds = new Set();
    groups.flat().forEach((entry) => {
        if (!entry?.id || seenIds.has(entry.id) || isPlaceholderEntry(entry)) return;
        seenIds.add(entry.id);
        merged.push(entry);
    });
    return merged;
};

/** Apply the same week-exception resolution used by the class scheduler calendar. */
const filterEntriesForSelectedWeek = (entries, weekNumber, weekYear) => {
    const list = (Array.isArray(entries) ? entries : []).filter((entry) => (
        entry && typeof entry.isScheduled === 'function' && entry.isScheduled()
    ));

    if (!weekNumber) {
        return list;
    }

    return list
        .map((item) => {
            if (typeof item.getEntryForWeek === 'function') {
                const result = item.getEntryForWeek(weekNumber, weekYear);
                if (!result) return null;
                if (result.id !== item.id) {
                    try {
                        const resObj = typeof result.toObject === 'function' ? result.toObject() : result;
                        return new ScheduleEntry({ ...resObj, id: item.id });
                    } catch (_) {
                        return result;
                    }
                }
                return result;
            }
            return item;
        })
        .filter(Boolean)
        .filter((item) => {
            if (typeof item.isActiveForWeekSync === 'function') {
                return item.isActiveForWeekSync(weekNumber, weekYear);
            }
            return true;
        });
};

const resolveInitialPreviewDate = (selectedWeek, selectedYearForView) => {
    const context = deriveScheduleTimelineContext({
        selectedWeek,
        selectedYearForView,
        fallbackDate: new Date(),
    });
    return context.date instanceof Date ? context.date : new Date();
};

const DraftLiveConfirmModal = ({
    isOpen,
    onClose,
    onConfirm,
    isConfirming = false,
    makeLive,
    draftName,
    periodName,
    periodId = null,
    // Preview data sources
    assetManager = null,
    currentDraftName = null,
    scheduledInstances = [],
    timePeriods = [],
    yearStructure = null,
    timeFrameSelection = null,
    selectedWeek = null,
    selectedYearForView = new Date().getFullYear(),
    activeDays = [],
    rooms = [],
    user = null,
    teachers = [],
    groups = [],
    subjects = [],
}) => {
    const periodLabel = periodName || 'this period';
    const draftLabel = draftName || 'this draft';

    const title = makeLive ? 'Make this draft live?' : 'Remove this draft from live?';

    const message = makeLive
        ? `Students and teachers will immediately see the schedule from "${draftLabel}" for ${periodLabel}. This becomes the live schedule shown in the app for that period.`
        : `Students and teachers will no longer see the schedule from "${draftLabel}" for ${periodLabel}. The live schedule for that period will change immediately.`;

    const [showPreview, setShowPreview] = useState(false);
    const [previewEntries, setPreviewEntries] = useState(null);
    const [isPreviewLoading, setIsPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState(null);
    const [previewDate, setPreviewDate] = useState(() => (
        resolveInitialPreviewDate(selectedWeek, selectedYearForView)
    ));

    // Reset preview when the modal closes
    useEffect(() => {
        if (!isOpen) {
            setShowPreview(false);
            setPreviewEntries(null);
            setPreviewError(null);
            setIsPreviewLoading(false);
        }
    }, [isOpen]);

    useEffect(() => {
        setPreviewEntries(null);
        setPreviewError(null);
    }, [draftName, periodId, makeLive]);

    // Sync preview week to the scheduler's current week when opening preview
    useEffect(() => {
        if (showPreview) {
            setPreviewDate(resolveInitialPreviewDate(selectedWeek, selectedYearForView));
        }
    }, [showPreview, selectedWeek, selectedYearForView]);

    const targetPeriod = useMemo(() => (
        periodId != null
            ? timePeriods.find((period) => String(period.id) === String(periodId)) || null
            : null
    ), [periodId, timePeriods]);

    // Periods overlapping the draft period's full range (so week-by-week nav stays accurate)
    const overlappingPeriodIds = useMemo(() => {
        if (targetPeriod) {
            const periodWeeks = PeriodManager.getWeeksForPeriod(targetPeriod, yearStructure);
            if (periodWeeks.length > 0) {
                const first = periodWeeks[0];
                const last = periodWeeks[periodWeeks.length - 1];
                return PeriodManager.getPeriodsOverlappingWeekRange(
                    timePeriods,
                    first.week,
                    first.year,
                    last.week,
                    last.year,
                    yearStructure
                ).map((period) => period.id);
            }
        }

        if (!timeFrameSelection) {
            return periodId != null ? [periodId] : [];
        }

        return PeriodManager.getPeriodIdsOverlappingClassSchedulerTimeFrame(
            timePeriods,
            timeFrameSelection,
            yearStructure
        );
    }, [periodId, targetPeriod, timeFrameSelection, timePeriods, yearStructure]);

    const loadPreviewEntries = useCallback(async () => {
        if (!assetManager || periodId == null) {
            setPreviewError('Preview is unavailable for this draft.');
            setPreviewEntries([]);
            return;
        }

        setIsPreviewLoading(true);
        setPreviewError(null);

        try {
            const otherPeriodIds = overlappingPeriodIds.filter(
                (id) => String(id) !== String(periodId)
            );

            const otherEntryGroups = await Promise.all(
                otherPeriodIds.map((otherPeriodId) => assetManager.fetchScheduleEntries(
                    { active_period_id: otherPeriodId },
                    '',
                    { forceNetwork: false }
                ))
            );

            let draftEntries = [];
            if (makeLive) {
                const useOpenDraft = Boolean(
                    draftName
                    && currentDraftName
                    && draftName === currentDraftName
                    && Array.isArray(scheduledInstances)
                );
                if (useOpenDraft) {
                    draftEntries = scheduledInstances;
                } else if (draftName) {
                    draftEntries = await assetManager.fetchScheduleEntriesForDraft(draftName);
                }
            }

            setPreviewEntries(mergeUniqueEntries([draftEntries, ...otherEntryGroups]));
        } catch (error) {
            setPreviewError(error?.message || 'Failed to load calendar preview.');
            setPreviewEntries([]);
        } finally {
            setIsPreviewLoading(false);
        }
    }, [
        assetManager,
        currentDraftName,
        draftName,
        makeLive,
        overlappingPeriodIds,
        periodId,
        scheduledInstances
    ]);

    useEffect(() => {
        if (!isOpen || !showPreview) return undefined;
        if (previewEntries !== null || isPreviewLoading) return undefined;
        loadPreviewEntries();
        return undefined;
    }, [isOpen, showPreview, previewEntries, isPreviewLoading, loadPreviewEntries]);

    const previewWeekNumber = useMemo(() => getWeekNumber(previewDate), [previewDate]);
    const previewWeekYear = useMemo(() => {
        // ISO week-year: use Thursday of the week (same approach as getWeekNumber)
        const d = new Date(Date.UTC(previewDate.getFullYear(), previewDate.getMonth(), previewDate.getDate()));
        d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
        return d.getUTCFullYear();
    }, [previewDate]);

    const visiblePreviewEntries = useMemo(
        () => filterEntriesForSelectedWeek(previewEntries || [], previewWeekNumber, previewWeekYear),
        [previewEntries, previewWeekNumber, previewWeekYear]
    );

    const currentWeekLabel = useMemo(
        () => formatWeekLabel(getWeekStartDate(previewDate)),
        [previewDate]
    );

    const otherPeriodCount = Math.max(0, overlappingPeriodIds.length - (periodId != null ? 1 : 0));

    // Match LiveScheduleView: never show rooms that are hidden from the live/public schedule.
    const livePreviewRooms = useMemo(() => (
        (Array.isArray(rooms) ? rooms : []).filter((room) => {
            const publicVisible = room?.publicVisible ?? room?.public_visible;
            return publicVisible !== false;
        })
    ), [rooms]);

    const previewHint = makeLive
        ? otherPeriodCount > 0
            ? `Shows "${draftLabel}" for ${periodLabel}, plus live schedules from ${otherPeriodCount} overlapping period${otherPeriodCount === 1 ? '' : 's'} — as students and teachers would see it.`
            : `Shows the calendar with "${draftLabel}" as the live schedule for ${periodLabel}.`
        : otherPeriodCount > 0
            ? `Shows the calendar without "${draftLabel}" — only live schedules from other overlapping periods remain.`
            : `Shows the calendar with no live schedule for ${periodLabel}.`;

    const handleTogglePreview = () => {
        setShowPreview((prev) => !prev);
    };

    const handlePrevWeek = useCallback(() => {
        setPreviewDate((prevDate) => {
            const next = new Date(prevDate);
            next.setDate(prevDate.getDate() - 7);
            return next;
        });
    }, []);

    const handleNextWeek = useCallback(() => {
        setPreviewDate((prevDate) => {
            const next = new Date(prevDate);
            next.setDate(prevDate.getDate() + 7);
            return next;
        });
    }, []);

    const handleWeekSelect = useCallback((selectedDate) => {
        if (selectedDate instanceof Date && !Number.isNaN(selectedDate.getTime())) {
            setPreviewDate(selectedDate);
        }
    }, []);

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={title}
            containerClassName={showPreview ? 'max-w-6xl max-h-[92vh]' : 'max-w-4xl max-h-[80vh]'}
        >
            <div className="space-y-4">
                <p className="text-sm text-gray-600">{message}</p>
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                    This affects the live schedule right away. Anyone viewing the schedule for {periodLabel} will see the change without needing to refresh.
                </div>

                {showPreview && (
                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-3">
                        <div className="mb-2 flex flex-wrap items-start justify-between gap-2">
                            <div className="min-w-0">
                                <div className="text-sm font-medium text-slate-800">
                                    {makeLive ? 'Preview as live' : 'Preview after removing'}
                                </div>
                                <div className="text-xs text-slate-500 mt-0.5">{previewHint}</div>
                            </div>
                            {isPreviewLoading && (
                                <span className="text-xs text-slate-500">Loading…</span>
                            )}
                        </div>

                        <div className="mb-3 flex justify-center">
                            <WeekNavigator
                                currentWeekLabel={currentWeekLabel}
                                onPrevWeek={handlePrevWeek}
                                onNextWeek={handleNextWeek}
                                currentDate={previewDate}
                                onWeekSelect={handleWeekSelect}
                                compact
                            />
                        </div>

                        {previewError && (
                            <div className="mb-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                                {previewError}
                            </div>
                        )}

                        {!isPreviewLoading && !previewError && previewEntries !== null && (
                            visiblePreviewEntries.length === 0 ? (
                                <div className="rounded border border-dashed border-slate-300 bg-white px-4 py-10 text-center text-sm text-slate-500">
                                    No scheduled entries would appear for this week.
                                </div>
                            ) : (
                                <SharedScrollProvider>
                                    <div className="max-h-[50vh] overflow-y-auto space-y-3 rounded border border-slate-200 bg-white p-2">
                                        {(activeDays || []).map((day) => {
                                            const itemsForDay = visiblePreviewEntries.filter(
                                                (item) => item.day === day
                                            );
                                            return (
                                                <DayTableSimple
                                                    key={`live-preview-${day}-${previewWeekNumber}-${previewWeekYear}`}
                                                    day={day}
                                                    rooms={livePreviewRooms}
                                                    items={itemsForDay}
                                                    dayScheduleEntry={visiblePreviewEntries}
                                                    user={user}
                                                    onOpenModal={noop}
                                                    onItemDrop={noop}
                                                    draggedItemInfo={EMPTY_DRAG_INFO}
                                                    onGlobalDragStart={noop}
                                                    onGlobalDragEnd={noop}
                                                    KEY_SEPARATOR={KEY_SEPARATOR}
                                                    teachers={teachers}
                                                    groups={groups}
                                                    subjects={subjects}
                                                    currentWeekNumber={previewWeekNumber}
                                                    currentYear={previewWeekYear}
                                                    showMondayHeader={day === 'monday'}
                                                    readOnly
                                                    compact
                                                />
                                            );
                                        })}
                                    </div>
                                </SharedScrollProvider>
                            )
                        )}
                    </div>
                )}
            </div>

            <div className="mt-6 flex flex-wrap justify-end gap-3">
                <Button
                    variant="success"
                    onClick={handleTogglePreview}
                    disabled={isConfirming}
                    aria-expanded={showPreview}
                    className="inline-flex items-center gap-2"
                >
                    {showPreview
                        ? <HiEyeSlash className="h-4 w-4" aria-hidden="true" />
                        : <HiEye className="h-4 w-4" aria-hidden="true" />}
                    {showPreview ? 'Hide Preview' : 'Preview Calendar'}
                </Button>
                <Button variant="secondary" onClick={onClose} disabled={isConfirming}>
                    Cancel
                </Button>
                <Button
                    variant={makeLive ? 'primary' : 'danger'}
                    onClick={onConfirm}
                    disabled={isConfirming}
                >
                    {isConfirming
                        ? (makeLive ? 'Making live…' : 'Removing…')
                        : (makeLive ? 'Make Live' : 'Remove from Live')}
                </Button>
            </div>
        </Modal>
    );
};

export default DraftLiveConfirmModal;
